import { getPrimaryColor, Styles } from '@orcar/common-d2c';
import styled from 'styled-components';
import { app } from '@/constants/common';
import { Accordion } from './guideUi';

const BLUE = getPrimaryColor(app);
const RED = Styles.COLOR.RED;
const GREEN = '#1FA97B';

const Check = (
  <svg viewBox='0 0 24 24' fill='none' aria-hidden>
    <circle cx='12' cy='12' r='10' fill={BLUE} />
    <path
      d='M7 12.5l3 3 7-7'
      fill='none'
      stroke='#fff'
      strokeWidth='2.2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

/**
 * 보험안내 — 이미지 대신 네이티브 HTML/CSS 인포그래픽.
 * 실제 텍스트라 SEO·스크린리더 OK, 모바일에서 카드가 리플로우되어 가독성 유지.
 */
const InsuranceGuide = () => {
  return (
    <Wrap>
      {/* 헤더 */}
      <Header>
        <HeaderArt aria-hidden>
          <svg viewBox='0 0 48 48' fill='none'>
            <path
              d='M24 4l16 6v10c0 10-7 17-16 20-9-3-16-10-16-20V10l16-6z'
              fill='rgba(255,255,255,0.16)'
              stroke='#fff'
              strokeWidth='2'
              strokeLinejoin='round'
            />
            <path
              d='M15 27l2-5a3 3 0 0 1 2.8-2h8.4a3 3 0 0 1 2.8 2l2 5'
              stroke='#fff'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <rect
              x='15'
              y='27'
              width='18'
              height='6'
              rx='2'
              stroke='#fff'
              strokeWidth='2'
            />
            <circle cx='19' cy='33' r='1.4' fill='#fff' />
            <circle cx='29' cy='33' r='1.4' fill='#fff' />
          </svg>
        </HeaderArt>
        <HeaderTitle>제주로렌터카 보험안내</HeaderTitle>
        <HeaderSub>
          복잡한 보험, <b>30초</b> 만에 핵심만 확인하세요!
        </HeaderSub>
        <Badges>
          <Badge>🚗 완전면책 (보상 무제한)</Badge>
          <Badge>📋 일반/고급 자차 차이 확인</Badge>
          <Badge warn>⚠️ 보장 제외 항목 꼭 체크</Badge>
        </Badges>
      </Header>

      {/* 1. 가장 많이 보는 보장 */}
      <Section>
        <SecHead>
          <Num>1</Num> 가장 많이 보는 보장
        </SecHead>
        <Card>
          <CardHead>완전면책</CardHead>
          <CheckList>
            <li>{Check} 자차 보상한도 무제한</li>
            <li>{Check} 자기부담금 없음</li>
            <li>{Check} 휴차보상료 없음</li>
            <li>{Check} 단독사고 보상 가능</li>
            <li>{Check} 현장출동 서비스 포함</li>
          </CheckList>
        </Card>
        <Note>※ 실제 요금은 차종·예약 화면에서 확인하세요.</Note>
      </Section>

      {/* 2. 자차면책 한눈에 */}
      <Section>
        <SecHead>
          <Num>2</Num> 자차면책 한눈에
        </SecHead>
        <ThreeCol>
          <MiniCard>
            <MiniTitle>일반면책</MiniTitle>
            <MiniBig>
              자기부담금 50만원
              <small>단독사고 보상 제외</small>
            </MiniBig>
            <MiniDesc>휴차보상료 50% 부담 · 보상한도 500만원</MiniDesc>
          </MiniCard>
          <MiniCard>
            <MiniTitle>고급면책</MiniTitle>
            <MiniBig>
              자기부담금 100만원
              <small>단독사고 보상 포함</small>
            </MiniBig>
            <MiniDesc>휴차보상료 없음 · 보상한도 1,500만원</MiniDesc>
          </MiniCard>
          <MiniCard accent={GREEN}>
            <MiniTitle>완전면책</MiniTitle>
            <MiniBig money>무제한</MiniBig>
            <MiniDesc>자기부담금·휴차 없음 · 단독사고 보상</MiniDesc>
          </MiniCard>
        </ThreeCol>
        <InfoLine>
          ⓘ 운전자 과실로 인한 차량 손해는 임차인의 책임이며, 자차면책은
          선택사항입니다.
        </InfoLine>
      </Section>

      {/* 3. 의무 가입 보험(자동차종합보험) */}
      <Section>
        <SecHead>
          <Num>3</Num> 의무 가입 보험 안내
        </SecHead>
        <NoticeList>
          <li>
            {Check} 렌터카는 기본적으로 자동차종합보험(대인·대물·자손)에
            가입되어 있습니다.
          </li>
          <li>
            {Check} 보험 접수 시 대인·대물·자손 항목별 자기부담금 50만원이 각각
            적용되며, 쌍방사고는 과실 비율에 따라 일부 환급됩니다.
          </li>
        </NoticeList>
        <TableTitle>자동차종합보험 보상 범위</TableTitle>
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>구분</Th>
                <Th>보상범위</Th>
                <Th>적용대상</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td strong>대인</Td>
                <Td>무제한</Td>
                <Td>제1·2 운전자 제외 사고 현장 사람</Td>
              </tr>
              <tr>
                <Td strong>대물</Td>
                <Td>1건당 2,000만원까지</Td>
                <Td>사고 당한 상대 측 차량이나 물건</Td>
              </tr>
              <tr>
                <Td strong>자손</Td>
                <Td>1인당 1,500만원까지</Td>
                <Td>제1·2 운전자</Td>
              </tr>
            </tbody>
          </Table>
        </TableScroll>
      </Section>

      {/* 4. 보장 제외 (기본 접힘 — 화면은 깔끔, 내용은 DOM에 유지) */}
      <Accordion num={4} title='이건 보장되지 않아요' count={9} red>
        <Chips>
          {[
            '고의 사고',
            '무면허 · 음주 · 약물 운전',
            '제3자 운전 사고',
            '정원초과 / 미보고 사고',
            '우도 내 사고',
            '타이어 펑크 · 파손',
            '사고견인 / 현장출동 비용은 자차 미적용',
            '일반면책는 단독사고 보상 제외',
            '침수 · 해양입수 등은 보장 제외',
          ].map((t) => (
            <Chip key={t}>{t}</Chip>
          ))}
        </Chips>
      </Accordion>

      {/* 5. 11대 중과실 (기본 접힘) */}
      <Accordion num={5} title='11대 중과실 위반도 면책 불가' count={11} red>
        <Chips>
          {[
            '신호 · 지시 위반',
            '중앙선 침범',
            '불법유턴',
            '20km 이상 과속',
            '앞지르기 방법 위반',
            '철길건널목 통과방법 위반',
            '횡단보도 사고',
            '무면허 · 음주운전',
            '보도 침범',
            '승객추락방지 의무 위반',
            '어린이보호구역 안전운전 위반',
          ].map((t) => (
            <Chip key={t}>{t}</Chip>
          ))}
        </Chips>
      </Accordion>
    </Wrap>
  );
};

export default InsuranceGuide;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  border-radius: 16px;
  padding: 28px 24px;
  color: #fff;
  background: linear-gradient(135deg, ${BLUE}, ${getPrimaryColor(app)}cc);
  text-align: center;
`;

const HeaderArt = styled.div`
  width: 60px;
  margin: 0 auto 10px;

  svg {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const HeaderTitle = styled.h2`
  ${Styles.FONT.H2};
  margin: 0;
  color: #fff;
`;

const HeaderSub = styled.p`
  ${Styles.FONT.B1};
  margin: 8px 0 18px;
  color: #fff;
  opacity: 0.95;
`;

const Badges = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Badge = styled.span<{ warn?: boolean }>`
  ${Styles.FONT.B2};
  font-weight: 700;
  padding: 8px 14px;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.16);
  color: #fff;
  border: 1px solid
    ${({ warn }) => (warn ? 'rgba(255,220,120,0.7)' : 'rgba(255,255,255,0.3)')};
`;

const Section = styled.section`
  border: 1px solid ${Styles.COLOR.GREY_05};
  border-radius: 16px;
  padding: 22px;
`;

const SecHead = styled.h3<{ red?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  ${Styles.FONT.H3};
  color: ${({ red }) => (red ? RED : Styles.COLOR.GREY_01)};
`;

const Num = styled.span<{ red?: boolean }>`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  ${Styles.FONT.B1};
  font-weight: 800;
  color: #fff;
  background-color: ${({ red }) => (red ? RED : BLUE)};
`;

const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid ${BLUE}33;
  border-radius: 14px;
  padding: 20px;
  background-color: ${BLUE}0a;
`;

const CardHead = styled.div`
  display: inline-block;
  ${Styles.FONT.B1};
  font-weight: 800;
  color: #fff;
  background-color: ${BLUE};
  padding: 6px 16px;
  border-radius: 999px;
  margin-bottom: 14px;
`;

const CheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    ${Styles.FONT.B1};
    color: ${Styles.COLOR.GREY_01};
  }

  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
  }
`;

const Note = styled.p`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_03};
  margin: 14px 0 0;
`;

const NoticeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  display: grid;
  gap: 12px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    ${Styles.FONT.B1};
    color: ${Styles.COLOR.GREY_01};
    line-height: 1.5;
  }

  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
    margin-top: 2px;
  }
`;

const TableTitle = styled.h4`
  ${Styles.FONT.B1};
  font-weight: 800;
  color: ${BLUE};
  margin: 0 0 12px;
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_01};

  th,
  td {
    border: 1px solid ${Styles.COLOR.GREY_05};
    padding: 12px 14px;
    text-align: left;
    vertical-align: middle;
    word-break: keep-all;
  }
`;

const Th = styled.th`
  background-color: ${Styles.COLOR.GREY_BG};
  ${Styles.FONT.B2};
  font-weight: 800;
  color: ${Styles.COLOR.GREY_02};
  white-space: nowrap;
`;

const Td = styled.td<{ strong?: boolean }>`
  font-weight: ${({ strong }) => (strong ? 800 : 500)};
  color: ${({ strong }) =>
    strong ? Styles.COLOR.GREY_01 : Styles.COLOR.GREY_02};
  ${({ strong }) => strong && 'white-space: nowrap;'}
`;

const MiniCard = styled.div<{ accent?: string }>`
  border: 1px solid
    ${({ accent }) => (accent ? `${accent}40` : Styles.COLOR.GREY_05)};
  border-radius: 14px;
  padding: 20px;
  text-align: center;
  background-color: ${({ accent }) =>
    accent ? `${accent}0d` : Styles.COLOR.GREY_BG};
`;

const MiniTitle = styled.div`
  ${Styles.FONT.B1};
  font-weight: 800;
  color: ${Styles.COLOR.GREY_01};
  margin-bottom: 10px;
`;

const MiniBig = styled.div<{ money?: boolean }>`
  ${Styles.FONT.H4};
  font-weight: 800;
  color: ${({ money }) => (money ? GREEN : Styles.COLOR.GREY_01)};
  line-height: 1.3;

  small {
    display: block;
    ${Styles.FONT.B2};
    font-weight: 500;
    color: ${Styles.COLOR.GREY_03};
  }
`;

const MiniDesc = styled.div`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_03};
  margin-top: 8px;
`;

const InfoLine = styled.p`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_02};
  background-color: ${BLUE}0a;
  border-radius: 10px;
  padding: 12px 14px;
  margin: 16px 0 0;
`;

const Chips = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

const Chip = styled.div`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_01};
  background-color: ${RED}0a;
  border: 1px solid ${RED}26;
  border-radius: 10px;
  padding: 12px 14px;
`;
